import {
  CancelledError,
  QueryFunctionContext,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';

import { RequestAbortError } from '@/gateways/RequestAbortError';

type QueryKey<Input> = [string, Input];
export type InputQueryFunction<T, Input> = (
  input: Input,
  context: QueryFunctionContext<QueryKey<Input>>,
) => Promise<T>;

type Props<T, Input, Data> = {
  parentKey: string;
  input: Input;
  query: InputQueryFunction<T, Input>;
} & Omit<Partial<UseQueryOptions<T, Error, Data, QueryKey<Input>>>, 'queryFn' | 'queryKey'>;

export function useQueryWithInput<T, Input, Data = T>({
  parentKey,
  input,
  query,
  ...rest
}: Props<T, Input, Data>) {
  return useQuery({
    queryKey: [parentKey, input] as QueryKey<Input>,
    queryFn: async (context) => {
      try {
        return await query(input, context);
      } catch (error) {
        if (error instanceof RequestAbortError) {
          throw new CancelledError({ revert: true, silent: true });
        }

        throw error;
      }
    },
    ...rest,
  });
}
